/**
 * @module app.constants
 */
import {proj as proj21781} from '@geoblocks/proj/src/EPSG_21781.js';

/**
 * @const {ol.proj.Projection}
 */
export const MAP_PROJECTION = proj21781;

/**
 * @enum {string}
 */
export const MAIN_MENU_EVENT_NAME = {
  SUB_MENU_LOADED: 'SUB_MENU_LOADED',
  MAIN_MENU_SHOWN: 'MAIN_MENU_SHOWN',
  EXPAND: 'EXPAND_MAIN_MENU',
  COLLAPSE: 'COLLAPSE_MAIN_MENU',
  COLLAPSE_SUB_MENU: 'COLLAPSE_SUB_MENU',
  INIT_COLLAPSED: 'INIT_COLLAPSED'
};

/**
 * @enum {string}
 */
export const TRACK_EVENT_NAME = {
  CANCEL_DRAWING: 'CANCEL_DRAWING',
  DELETE_LAST_POINT: 'DELETE_LAST_POINT',
  FINISH_DRAWING: 'FINISH_DRAWING'
};

/**
 * @enum {string}
 */
export const TRACK_POI_EVENT_NAME = {
  SET_EDITING_STATE: 'POI_SET_EDITING_STATE',
  FINISH_DRAWING: 'POI_FINISH_DRAWING'
};

/**
 * @enum {number}
 */
export const TRACK_FILTER_ID = {
  OTHER: -1,
  ALL: -2
};

/**
 * @enum {string}
 */
export const TRACK_COLOR = {
  RED: 'red',
  ORANGE: 'orange',
  YELLOW: 'yellow',
  YELLOWGREEN: 'yellowgreen',
  GREEN: 'green',
  DARKGREEN: 'darkgreen',
  LIGHTBLUE: 'lightblue',
  DARKBLUE: 'darkblue',
  PURPLE: 'purple',
  BLACK: 'black',
};

/**
 * @const {app.constants.TRACK_COLOR}
 */
export const TRACK_DEFAULT_COLOR = TRACK_COLOR.RED;

/**
 * @const {!Object<app.constants.TRACK_COLOR, !appx.TrackColorDefinition>}
 */
export const TRACK_COLOR_VALUES = {
  [TRACK_COLOR.RED]: {
    color: '#e30613',
    tickColor: '#fff',
  },
  [TRACK_COLOR.ORANGE]: {
    color: '#f39200',
    tickColor: '#fff',
  },
  [TRACK_COLOR.YELLOW]: {
    color: '#ffe500',
    tickColor: '#1d1d1b',
  },
  [TRACK_COLOR.YELLOWGREEN]: {
    color: '#95c11f',
    tickColor: '#fff',
  },
  [TRACK_COLOR.GREEN]: {
    color: '#009139',
    tickColor: '#fff',
  },
  [TRACK_COLOR.DARKGREEN]: {
    color: '#006633',
    tickColor: '#fff',
  },
  [TRACK_COLOR.LIGHTBLUE]: {
    color: '#009fe3',
    tickColor: '#fff',
  },
  [TRACK_COLOR.DARKBLUE]: {
    color: '#0065ae',
    tickColor: '#fff',
  },
  [TRACK_COLOR.PURPLE]: {
    color: '#662483',
    tickColor: '#fff',
  },
  [TRACK_COLOR.BLACK]: {
    color: '#1d1d1b',
    tickColor: '#fff',
  },
};

/**
 * @const {string}
 */
export const MENU_TOGGLE_COLLAPSE = 'MENU_TOGGLE_COLLAPSE';

/**
 * @const {number}
 * This constant establishes the correspondence between WMTS zoom level and printed map scale.
 * E.g. WMTS zoom level 21 with resolution 5 m / px will be used to print at 1 : 50000 scale.
 * The content of zoom level 21 tiles corresponds to the map style and level of generalization
 * of 1 : 50000 paper maps.
 * Another way of stating it is that Swisstopo WMTS maps are printed at 254 dpi.
 * By using the same correspondence for vector features, we make sure that their size relative
 * to the base map is the same in print and on screen.
 */
export const SWISSTOPO_PIXELS_PER_METER = 10000;

/**
 * @const {number}
 */
export const POINTS_PER_INCH = 72;

/**
 * @const {number}
 * According to the "international yard" definition 1 inch is defined as exactly 2.54 cm.
 */
export const METERS_PER_INCH = 0.0254;

/**
 * @const {number}
 */
export const POINTS_PER_METER = POINTS_PER_INCH / METERS_PER_INCH;

/**
 * @const {number}
 */
export const PIXELS_PER_POINT =
  SWISSTOPO_PIXELS_PER_METER * METERS_PER_INCH / POINTS_PER_INCH;

/**
 * @enum {number}
 */
export const Z_INDEX = {
  PHOTOS: 10,
  DETOURS_LINE: 7,
  DETOURS_POINT: 9,
  ROUTE_LOGOS: 8,
  HIGHLIGHT: 6,
  UP: 5,
  SLOWUP: 3.1,
  HIKING_TRAILS: 3,
  ROUTE: 2,
  OVERLAY: 1.2,
  ADMIN: 1,
  BASEMAP: 0,
};

/**
 * @const
 * @type {Object.<string, string>}
 */
export const LAND_TO_LAYER = {
  'velo': 'Veloland',
  'wander': 'Wanderland',
  'mountainbike': 'Mountainbikeland',
  'skating': 'Skatingland',
  'kanu': 'Kanuland',
  'winterhiking': 'Winterhiking',
  'snowshoe': 'Snowshoe',
  'crosscountry': 'Crosscountry',
  'sledging': 'Sledging',
  // hacks
  'mtb': 'Mountainbikeland',
  'schweizmobil': 'Veloland'
};

export const ORDERED_LANDS = [
  'wander', 'velo', 'mountainbike', 'skating', 'kanu', 'winterhiking',
  'snowshoe', 'crosscountry', 'sledging'
];

export const OPACITY = {
  'crosscountry': 0.7
};


/**
 * @const
 * @type {Array.<string>}
 */
export const WMTS_GROUPS = Object.values(LAND_TO_LAYER);


/**
 * @const
 * @type {Object.<string, string>}
 */
export const LAND_TO_DOMAIN_NAME = {
  'velo': 'www.veloland.ch',
  'wander': 'www.wanderland.ch',
  'mtb': 'www.mountainbikeland.ch',
  'skating': 'www.skatingland.ch',
  'kanu': 'www.kanuland.ch',
  'schweizmobil': 'www.schweizmobil.ch'
};


/**
 * @enum {string}
 */
export const ROUTE_SCOPE = {
  NATIONAL: 'National',
  REGIONAL: 'Regional',
  LOCAL: 'Lokal',
  HINT: 'Tipp',
  HANDICAP: 'Handicap',
};


/**
 * @const {string}
 */
export const STAGES = 'Etappen';


/**
 * @const
 * @type {Object.<string, Array.<string>>}
 */
export const LAND_TO_LAYER_TYPE = {
  'velo': ['National', 'Regional', 'Lokal', 'Tipp'],
  'wander': ['National', 'Regional', 'Lokal', 'Handicap'],
  'mtb': ['National', 'Regional', 'Lokal'],
  'skating': ['National', 'Regional', 'Lokal'],
  'kanu': ['National', 'Regional'],
  'winterhiking': ['Lokal'],
  'snowshoe': ['Lokal'],
  'crosscountry': ['Lokal'],
  'sledging': ['Lokal'],
  'schweizmobil': ['National', 'Regional', 'Lokal', 'Tipp']
};


/**
 * @const
 * @type {Object.<string, Array.<string>>}
 */
export const DIRECT_LINKS = {
  'Place': ['place', 'orte'],
  'Velorental': ['velorental', 'mietstation'],
  'Cycleservice': ['cycleservice', 'servicestelle'],
  'Sightseeing': ['poi', 'sightseeing'],
  // Search for all type of station
  'Train': ['vkpt', 'station'],
  'Bus': ['vkpt', 'station'],
  'Trambus': ['vkpt', 'station'],
  'Boat': ['vkpt', 'station'],
  'Cableway': ['vkpt', 'station'],
  'Chairlift': ['vkpt', 'station'],
  'Telecabin': ['vkpt', 'station'],
  'Funicular': ['vkpt', 'station'],

  // Search for all type of accomodation
  'Hotel': ['uebernachten', 'accomodation', 'accommodation'],
  'Bedbreakfast': ['uebernachten', 'accomodation', 'accommodation'],
  'Youthhostel': ['uebernachten', 'accomodation', 'accommodation'],
  'Backpacker': ['uebernachten', 'accomodation', 'accommodation'],
  'Groupaccom': ['uebernachten', 'accomodation', 'accommodation'],
  'Sleepingstraw': ['uebernachten', 'accomodation', 'accommodation'],
  'Farmaccom': ['uebernachten', 'accomodation', 'accommodation'],
  'Vacationapptmt': ['uebernachten', 'accomodation', 'accommodation'],
  'Campingsite': ['uebernachten', 'accomodation', 'accommodation'],
  'Mountainhut': ['uebernachten', 'accomodation', 'accommodation'],

  'Alpentaxi': ['alpentaxi'],
  'Slowup': ['slowup'],
  'Swisspark': ['swisspark'],

  'MountainHiking': ['mountain_hiking'],
  'AlpinTour': ['alpine_tour'],
  'Climbing': ['climbing'],
  'ViaFerrata': ['via_ferrata'],
  'SkiTour': ['ski_tour']
};


/**
 * Replacement table from layers from permalink to layers from menu.
 * @const
 * @type {Object.<string, string>}
 */
export const LAYER_REPLACEMENT = {
  'mtbland': 'Mountainbikeland',
  'wanderland': 'Wanderland',
  'veloland': 'Veloland',
  'skatingland': 'Skatingland',
  'kanuland': 'Kanuland',
  'winterhikingroutenlokal': 'Winterhiking',
  'snowshoeroutenlokal': 'Snowshoe',
  'crosscountryroutenlokal': 'Crosscountry',
  'sledgingroutenlokal': 'Sledging',
  'winterhiking': 'Winterhiking',
  'snowshoe': 'Snowshoe',
  'crosscountry': 'Crosscountry',
  'sledging': 'Sledging'
};


/**
 * @const
 * @type {Array.<string>}
 */
export const MOUNTAINEERING_LAYERS = [
  'MountainHiking',
  'AlpinTour',
  'Climbing',
  'ViaFerrata',
  'SkiTour'
];


/**
 * @const
 * @type {Object.<string, string>}
 */
export const MOUNTAINEERING_TYPE_TO_LAYER = {
  'mountain_hiking': 'Mountain Hiking',
  'alpine_tour': 'Alpin Tour',
  'climbing': 'Climbing',
  'via_ferrata': 'Via Ferrata',
  'ski_tour': 'Ski Tour',
  'mountainhiking': 'Mountain Hiking',
  'alpinetour': 'Alpin Tour',
  'viaferrata': 'Via Ferrata',
  'skitour': 'Ski Tour'
};


/**
 * @const
 * @type {Array.<string>}
 */
export const LAYER_ORDER = [
  'Slowup',

  // mountaineering
  'Mountaineering', ...MOUNTAINEERING_LAYERS,

  // bahn bus schiff
  'Station', 'Train', 'Bus', 'Trambus', 'Boat', 'Cableway', 'Chairlift', 'Telecabin', 'Funicular', 'Alpentaxi',

  // accomodation
  'Accomodation', 'Hotel', 'Bedbreakfast', 'Youthhostel', 'Backpacker', 'Groupaccom',
  'Sleepingstraw', 'Farmaccom', 'Vacationapptmt', 'Mountainhut', 'Campingsite',

  // service
  'Services', 'Place', 'Sightseeing', 'Velorental', 'Cycleservice',

  // land layers
  '^Wander.*National', '^Wander.*Regional', '^Wander.*Lo[ck]al',
  '^Wander.*Handicap', '^Wander',
  '^Veloland.*National', '^Veloland.*Regional', '^Veloland.*Lo[ck]al',
  '^Veloland',
  '^Mtbland.*National', '^Mtbland.*Regional', '^Mtbland.*Lo[ck]al',
  '^Mtbland', '^Mountainbikeland',
  '^Skatingland.*National', '^Skatingland.*Regional', '^Skatingland.*Lo[ck]al',
  '^Skatingland',
  '^Kanuland.*National', '^Kanuland.*Regional', '^Kanuland.*Lo[ck]al',
  '^Kanuland',

  'Winterhiking', 'Snowshoe', 'Crosscountry', 'Sledging',

  'Slope',

  'NatureLandscape', 'Swisspark', 'AlpguardDog', 'WildlifeArea', 'WildlifeReserve'
];


/**
 * @const
 * @type {Array.<string>}
 */
export const LAYERGROUPS = [
  'Wanderland', 'Veloland', 'Mountainbikeland', 'Skatingland',
  'Kanuland', 'Station', 'Accomodation', 'Services',
  'NatureLandscape', 'Mountaineering', 'Crosscountry'
];


/**
 * @const
 * @type {Object.<string, Array.<appx.LayerDefinition>>}
 */
export const LAYER_TREE = {
  'Wanderland': [
    'WanderlandEtappenNational',
    'WanderlandEtappenRegional',
    'WanderlandEtappenLokal',
    'WanderlandEtappenHandicap',
    'Wanderwegnetz'
  ],
  'Veloland': [
    'VelolandEtappenNational',
    'VelolandEtappenRegional',
    'VelolandEtappenLokal'
  ],
  'Mountainbikeland': [
    'MtblandEtappenNational',
    'MtblandEtappenRegional',
    'MtblandEtappenLokal'
  ],
  'Skatingland': [
    'SkatinglandEtappenNational',
    'SkatinglandEtappenRegional',
    'SkatinglandEtappenLokal'
  ],
  'Kanuland': [
    'KanulandEtappenNational',
    'KanulandEtappenRegional'
  ],
  'Crosscountry': [
    'CrosscountryEtappenNational',
    'CrosscountryNetwork'
  ],
  'Station': [
    'Train',
    'Bus',
    'Trambus',
    'Boat',
    'Cableway',
    'Chairlift',
    'Telecabin',
    'Funicular',
    'Alpentaxi'
  ],
  'Accomodation': [
    'Hotel',
    'Bedbreakfast',
    'Youthhostel',
    'Backpacker',
    'Groupaccom',
    'Sleepingstraw',
    'Farmaccom',
    'Vacationapptmt',
    'Mountainhut',
    'Campingsite'
  ],
  'Services': [
    'Place',
    'Sightseeing',
    'Velorental',
    'Cycleservice',
  ],
  'NatureLandscape': [
    'Swisspark',
    'AlpguardDog',
    'WildlifeArea',
    'WildlifeReserve'
  ],
  'Mountaineering': [
    'MountainHiking',
    'AlpinTour',
    'Climbing',
    'ViaFerrata'
  ],
  'SkiTour': [
    'SkiTour'
  ],
};

/**
 * @const
 * @type {Array.<string>}
 */
export const VECTOR_LAYERS = [
  'Backpacker',
  'Bedbreakfast',
  'Boat',
  'Bus',
  'Cableway',
  'Chairlift',
  'Telecabin',
  'Campingsite',
  'Cycleservice',
  'Farmaccom',
  'Funicular',
  'Groupaccom',
  'Hotel',
  'Mountainhut',
  'Place',
  'Rufbus',
  'Seilbahn',
  'Sightseeing',
  'Sleepingstraw',
  'Taxi',
  'Alpentaxi', // fake, see Taxi, Rubfus, Seilbahn
  'Train',
  'Trambus',
  'Vacationapptmt',
  'Velorental',
  'Youthhostel',
  'Slowup', // for 3D
  ...MOUNTAINEERING_LAYERS
];

/**
 * @const
 * @type {Array.<string>}
 */
export const MVT_LAYERS = [
  'Wanderland',
  'WanderlandEtappenNational',
  'WanderlandEtappenRegional',
  'WanderlandEtappenLokal',
  'WanderlandEtappenHandicap',

  'Veloland',
  'VelolandEtappenNational',
  'VelolandEtappenRegional',
  'VelolandEtappenLokal',

  'Mountainbikeland',
  'MtblandEtappenNational',
  'MtblandEtappenRegional',
  'MtblandEtappenLokal',

  'Skatingland',
  'SkatinglandEtappenNational',
  'SkatinglandEtappenRegional',
  'SkatinglandEtappenLokal',

  'Kanuland',
  'KanulandEtappenNational',
  'KanulandEtappenRegional',

  'Winterhiking',
  'Snowshoe',
  'Crosscountry',
  'Sledging',
];

/**
 * @const
 * @type {Object.<string, string>}
 */
export const HOST_TO_LAND = {
  'map.veloland.ch': 'velo',
  'map.wanderland.ch': 'wander',
  'map.mountainbikeland.ch': 'mtb',
  'map.skatingland.ch': 'skating',
  'map.kanuland.ch': 'kanu',
  'map.schweizmobil.ch': 'schweizmobil'
};


/**
 * @const
 * @type {Array.<string>}
 */
export const LANDS = Object.values(HOST_TO_LAND).concat([
  'winterhiking', 'snowshoe', 'crosscountry', 'sledging'
]);


/**
 * @const
 * @type {Object.<string, string>}
 */
export const LAND_ALIASES = {
  'veloland': 'velo',
  'wanderland': 'wander',
  'mountainbikeland': 'mtb',
  'skatingland': 'skating',
  'kanuland': 'kanu'
};


/**
 * @const
 * @type {Object.<string, string>}
 */
export const LAND_LAYER_TO_PSEUDO_GROUP_LAYER = {
  'WanderlandEtappenNational': 'Wanderland',
  'WanderlandEtappenRegional': 'Wanderland',
  'WanderlandEtappenLokal': 'Wanderland',
  'WanderlandEtappenHandicap': 'Wanderland',
  'Wanderwegnetz': 'Wanderland',
  'VelolandEtappenNational': 'Veloland',
  'VelolandEtappenRegional': 'Veloland',
  'VelolandEtappenLokal': 'Veloland',
  'MtblandEtappenNational': 'Mountainbikeland',
  'MtblandEtappenRegional': 'Mountainbikeland',
  'MtblandEtappenLokal': 'Mountainbikeland',
  'SkatinglandEtappenNational': 'Skatingland',
  'SkatinglandEtappenRegional': 'Skatingland',
  'SkatinglandEtappenLokal': 'Skatingland',
  'KanulandEtappenNational': 'Kanuland',
  'KanulandEtappenRegional': 'Kanuland',
  'CrosscountryEtappenLokal': 'Crosscountry',
  'CrosscountryNetwork': 'Crosscountry',
  'Sledging': 'Sledging',
  'Snowshoe': 'Snowshoe',
  'Winterhiking': 'Winterhiking'
};

/**
 * @const {ol.Color}
 */
export const DETOUR_COLOR = [216, 0, 0, 0.6];

/**
 * @const {number}
 */
export const TRACK_WIDTH = 5;

/**
 * @const {number}
 */
export const HIGHLIGHT_SPACING = 20;

/**
 * @const {number}
 */
export const HIGHLIGHT_SPACING_SMALL = 60;

/**
 * @const {number}
 */
export const HIGHLIGHT_WIDTH = 10;

/**
 * @const {number}
 */
export const HIGHLIGHT_WIDTH_SMALL = 14;

/**
 * @const {number}
 */
export const SEARCH_LABEL_MAX_SIZE = 100;

/**
 * @const {string}
 */
export const FEATURE_CHOICE_CLASS_NAME = 'sl-choice';

/**
 * See OPTIONAL_PROPERTIES_UPDATE_FIELDS.
 * @type {Array.<string>}
 */
export const VALID_RECORDEDTRACK_PROPS_FOR_UPDATE = ['public', 'name'];

export const VECTOR_TILE_LAYERS = ['Slowup', 'Swisspark', 'CrosscountryNetwork'];

export const SWISSPARK_FILL_RESOLUTION = 35;

/**
 * @type {Array.<string>}
 */
export const SLOWUP_CATEGORIES = [
  'Aid_Post',
  'Bus_Station',
  'Climbing_Descent',
  'Direction_Arrow',
  'Fairground',
  'Info_Stand',
  'Parking',
  'Rental_Station',
  'Service_Station',
  'Ship_Station',
  'Stand_Procap',
  'Train_Station',
  'Wc',
  'Wc_Handicap'];

export const MAX_POINTS_FOR_A_TRACK = 10000;
